<template>
  <div class="overview-best-times ov-card" :class="{ 'overview-best-times--fill': fill }">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">access_time</i>
      <Tooltip :text="$t('dash.overview.help.best_times')" position="top">
        <h2 class="ov-help-title">{{ $t('dash.overview.best_times.title') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
    </div>

    <div v-if="loading" class="bt-skeleton"></div>
    <p v-else-if="!hasData" class="ov-empty">{{ $t('dash.overview.best_times.empty') }}</p>
    <template v-else>
      <div class="bt-highlights">
        <Tooltip v-if="best" :text="cellHelp(best, best.day, best.slot)" position="top">
          <span class="bt-pill bt-pill--best"><i class="material-icons">trending_up</i>{{ $t('dash.overview.best_times.best', { day: dayLabel(best.day), slot: slotLabel(best.slot) }) }}</span>
        </Tooltip>
        <Tooltip v-if="worst" :text="cellHelp(worst, worst.day, worst.slot)" position="top">
          <span class="bt-pill bt-pill--worst"><i class="material-icons">trending_down</i>{{ $t('dash.overview.best_times.worst', { day: dayLabel(worst.day), slot: slotLabel(worst.slot) }) }}</span>
        </Tooltip>
      </div>
      <div class="bt-grid">
        <span></span>
        <span v-for="slot in 6" :key="`h-${slot}`" class="bt-head">{{ slotShort(slot - 1) }}</span>
        <template v-for="(row, day) in cells">
          <span :key="`d-${day}`" class="bt-day">{{ dayLabel(day) }}</span>
          <Tooltip v-for="(cell, slot) in row" :key="`c-${day}-${slot}`" :text="cellHelp(cell, day, slot)" position="top" class="bt-cell-wrap">
            <span
              class="bt-cell"
              :style="{ background: cellColor(cell, day, slot) }"
              :class="{ 'bt-cell--empty': !cell.posts, 'bt-cell--best': isCell(best, day, slot), 'bt-cell--worst': isCell(worst, day, slot) }"
            >
              <i v-if="isCell(best, day, slot)" class="material-icons">star</i>
            </span>
          </Tooltip>
        </template>
      </div>
      <div class="bt-legend">
        <span class="bt-legend-empty"></span>
        <span>{{ $t('dash.overview.best_times.no_post') }}</span>
        <span class="bt-legend-sep"></span>
        <span>{{ $t('dash.overview.best_times.less') }}</span>
        <span class="bt-legend-scale"></span>
        <span>{{ $t('dash.overview.best_times.more') }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import Tooltip from '../tooltip/tooltipInit.vue'

export default {
  name: 'OverviewBestTimes',

  components: { Tooltip },

  props: {
    // 7 jours (lundi = 0) x 6 tranches de 4 h : { posts, engagement }
    grid: { type: Array, default: null },
    loading: { type: Boolean, default: false },
    // Tableau de bord personnalisé : la grille occupe la hauteur de la case
    fill: { type: Boolean, default: false }
  },

  computed: {
    // Engagement moyen par publication dans chaque créneau
    cells() {
      return (this.grid || []).map(row => row.map(c => ({ ...c, avg: c.posts ? c.engagement / c.posts : 0 })))
    },

    hasData() {
      return this.cells.some(row => row.some(c => c.posts > 0))
    },

    // Créneaux ayant au moins une publication, du moins bon au meilleur
    ranked() {
      const list = []
      this.cells.forEach((row, day) => row.forEach((c, slot) => { if (c.posts) list.push({ ...c, day, slot }) }))
      return list.sort((a, b) => a.avg - b.avg)
    },

    // Rang centile des créneaux avec engagement (0 = moins bon, 1 = meilleur), à égalité de moyenne même rang ;
    // les créneaux à 0 sont exclus du classement (toujours blancs)
    percentiles() {
      const engaged = this.ranked.filter(c => c.avg > 0)
      const n = engaged.length
      const map = {}
      engaged.forEach(c => {
        const firstIndex = engaged.findIndex(r => r.avg === c.avg)
        map[`${c.day}-${c.slot}`] = n > 1 ? firstIndex / (n - 1) : 1
      })
      return map
    },

    best() {
      return this.ranked.length ? this.ranked[this.ranked.length - 1] : null
    },

    // Moins bon créneau, seulement s'il se distingue du meilleur
    worst() {
      const worst = this.ranked.length > 1 ? this.ranked[0] : null
      return worst && this.best && worst.avg < this.best.avg ? worst : null
    }
  },

  methods: {
    dayLabel(day) {
      return moment().locale((this.$i18n && this.$i18n.locale) || 'fr').isoWeekday(day + 1).format('ddd')
    },

    slotLabel(slot) {
      return `${slot * 4}h – ${slot * 4 + 4}h`
    },

    slotShort(slot) {
      return `${slot * 4}h`
    },

    isCell(target, day, slot) {
      return !!target && target.day === day && target.slot === slot
    },

    /**
     * Prune Mashup selon le classement : moins bons en clair -> meilleurs en foncé.
     * Le classement plutôt que la valeur brute garde un contraste net même si un créneau écrase les autres.
     */
    cellColor(cell, day, slot) {
      if (!cell.posts) return 'transparent'
      // Aucun engagement : toujours blanc
      if (!cell.avg) return '#ffffff'
      const p = this.percentiles[`${day}-${slot}`]
      const nearWhite = [251, 248, 252]
      const plum = [107, 31, 77]
      // Courbe accentuée : les créneaux moyens restent clairs, seuls les meilleurs deviennent foncés
      const t = Math.pow(p, 1.6)
      const rgb = nearWhite.map((v, i) => Math.round(v + (plum[i] - v) * t))
      return `rgb(${rgb.join(', ')})`
    },


    cellHelp(cell, day, slot) {
      const when = `${this.dayLabel(day)} ${this.slotLabel(slot)}`
      if (!cell.posts) return `${when} : ${this.$t('dash.overview.best_times.no_post')}`
      return `${when} : ${this.$t('dash.overview.best_times.cell', { posts: cell.posts, engagement: cell.engagement.toLocaleString(), avg: Math.round(cell.avg).toLocaleString() })}`
    }
  }
}
</script>

<style scoped>
.bt-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  margin-bottom: 14px;
}
.bt-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.8px;
  font-weight: 600;
  color: #121826;
}
.bt-pill .material-icons {
  font-size: 16px;
}
.bt-pill--best .material-icons { color: #b64985; }
.bt-pill--worst { color: #555e75; font-weight: 500; }
.bt-pill--worst .material-icons { color: #9ba3b7; }
.bt-grid {
  display: grid;
  grid-template-columns: 40px repeat(6, minmax(0, 1fr));
  gap: 4px;
  align-items: center;
}
.bt-head,
.bt-day {
  font-size: 11px;
  color: #9ba3b7;
  text-transform: capitalize;
}
.bt-head {
  text-align: center;
}
.bt-cell-wrap {
  display: block;
}
.bt-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  border-radius: 4px;
  transition: transform 0.15s;
}
.overview-best-times--fill {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.overview-best-times--fill .bt-grid {
  flex: 1;
  min-height: 0;
  grid-template-rows: auto repeat(7, minmax(14px, 1fr));
  align-items: stretch;
}
.overview-best-times--fill .bt-day {
  align-self: center;
}
.overview-best-times--fill .bt-cell-wrap,
.overview-best-times--fill .bt-cell {
  height: 100%;
  min-height: 14px;
}
.overview-best-times--fill .bt-skeleton {
  flex: 1;
  height: auto;
}
.bt-cell .material-icons {
  font-size: 15px;
  color: #fff;
}
/* Créneau sans publication : case vide en pointillés, distincte d'un « moins bon » créneau */
.bt-cell--empty {
  border: 1px dashed #e0d0ea;
}
/* Les moins bons créneaux sont très clairs : un liseré garde la case visible */
.bt-cell:not(.bt-cell--empty) {
  border: 1px solid #efe7f3;
}
.bt-cell:hover {
  transform: scale(1.08);
}
.bt-cell--best {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #b64985;
}
.bt-cell--worst {
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #c9b8d4;
}
.bt-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 10px;
  font-size: 11px;
  color: #9ba3b7;
}
.bt-legend-scale {
  width: 100px;
  height: 8px;
  border-radius: 4px;
  border: 1px solid #efe7f3;
  background: linear-gradient(90deg, rgb(251, 248, 252), rgb(107, 31, 77));
}
.bt-legend-empty {
  width: 14px;
  height: 10px;
  border-radius: 3px;
  border: 1px dashed #e0d0ea;
}
.bt-legend-sep {
  width: 1px;
  height: 12px;
  margin: 0 4px;
  background: #e0d0ea;
}
.bt-skeleton {
  height: 220px;
  border-radius: 5.6px;
  background: #f1edf5;
}
</style>
